import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, LogOut, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { fetchAdminProducts, deleteProduct } from "@/lib/api";
import { AdminProductCard } from "@/components/admin/AdminProductCard";
import { ProductFormDialog } from "@/components/admin/ProductFormDialog";
import { RatesSettings } from "@/components/admin/RatesSettings";

const CATEGORIES = [
  { value: "gold", label: "Gold" },
  { value: "silver", label: "Silver" },
  { value: "diamond", label: "Diamond" },
  { value: "others", label: "Others" },
  { value: "offers", label: "Offers" },
  { value: "festival", label: "Festival" },
  { value: "banner", label: "Homepage Banner" },
  { value: "arrivals_video", label: "New Arrivals Video" },
];

export default function AdminDashboard() {
  const { username, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("gold");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    if (activeCategory === "rates") return; // Rates settings has its own independent data loading
    setLoading(true);
    try {
      const data = await fetchAdminProducts(activeCategory);
      setItems(data);
    } catch (e) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = () => {
    setEditingItem(null);
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProduct(deleteTarget.id);
      toast.success("Deleted successfully");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const currentLabel = CATEGORIES.find((c) => c.value === activeCategory)?.label || "";

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/images/jewellery/logo.png"
              alt="Vini Gems And Jewellers"
              className="h-10 w-10 rounded-full ring-1 ring-primary/40 object-contain"
            />
            <div>
              <p className="font-heading text-base font-semibold text-foreground leading-none">Admin Portal</p>
              <p className="text-xs text-muted-foreground mt-1">Signed in as {username}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-view-site-link">
                <ExternalLink className="h-3.5 w-3.5" /> View Site
              </a>
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Tabs value={activeCategory} onValueChange={setActiveCategory}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <TabsList className="bg-card border border-border p-1.5 h-auto rounded-full flex-wrap">
              {CATEGORIES.map((c) => (
                <TabsTrigger
                  key={c.value}
                  value={c.value}
                  className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
                  data-testid={`admin-tab-${c.value}`}
                >
                  {c.label}
                </TabsTrigger>
              ))}
              <TabsTrigger
                value="rates"
                className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
                data-testid="admin-tab-rates"
              >
                Rates
              </TabsTrigger>
            </TabsList>
            {activeCategory !== "rates" && (
              <Button variant="premium" onClick={handleAdd} data-testid="admin-add-btn">
                <Plus className="h-4 w-4" /> Add New
              </Button>
            )}
          </div>

          {CATEGORIES.map((c) => (
            <TabsContent key={c.value} value={c.value} className="mt-0">
              {c.value === "banner" && (
                <div className="mb-6 rounded-xl border border-primary/30 bg-primary/5 px-5 py-4 text-sm text-muted-foreground" data-testid="admin-banner-help">
                  <strong className="text-foreground">How this works:</strong> Add <strong>one</strong> item
                  here — its cover photo + up to 3 gallery photos become the rotating advertisement banner shown
                  near the top of your homepage. Clicking the banner always takes visitors to your New Arrivals
                  page. If you add more than one item, only the first (by display order) is shown.
                </div>
              )}
              {c.value === "arrivals_video" && (
                <div className="mb-6 rounded-xl border border-primary/30 bg-primary/5 px-5 py-4 text-sm text-muted-foreground" data-testid="admin-arrivals-video-help">
                  <strong className="text-foreground">How this works:</strong> Add <strong>one</strong> item
                  here and upload a video as its cover media — it plays large, autoplaying (muted, with an
                  unmute button) at the top of your New Arrivals &amp; Offers page only. It never appears on
                  the homepage. If you add more than one item, only the first (by display order) is shown.
                  Keep videos under ~15MB for fast loading — ask if you'd like help compressing one.
                </div>
              )}
              {activeCategory !== c.value ? null : loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="aspect-square rounded-xl" />
                  ))}
                </div>
              ) : items.length === 0 ? (
                <div className="text-center py-20 text-muted-foreground" data-testid={`admin-empty-${c.value}`}>
                  <p>No items yet in {c.label}.</p>
                  <Button variant="outline" className="mt-4" onClick={handleAdd}>
                    Add your first item
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5" data-testid={`admin-grid-${c.value}`}>
                  {items.map((item) => (
                    <AdminProductCard key={item.id} item={item} onEdit={handleEdit} onDelete={setDeleteTarget} />
                  ))}
                </div>
              )}
            </TabsContent>
          ))}

          <TabsContent value="rates" className="mt-0">
            <RatesSettings />
          </TabsContent>
        </Tabs>
      </main>

      <ProductFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        category={activeCategory}
        product={editingItem}
        onSaved={load}
      />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this item?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{deleteTarget?.name}&quot; will be permanently removed from the {currentLabel} section of the
              website. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="admin-confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
